import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { PageHeader, LoadingTable, ErrorState, EmptyState } from '../components/States';
import { DataTable } from '../components/DataTable';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import * as api from '../lib/api';
import { Plus, Lock } from 'lucide-react';

const MONTHS = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

export default function AnalisiCGAnalisi() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'control'; // sezione Analisi CG aperta al Controllo di gestione
  const nav = useNavigate();
  const [analyses, setAnalyses] = useState([]);
  const [schemas, setSchemas] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [dlg, setDlg] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const [a, s, c] = await Promise.all([api.cgaAnalyses(), api.cgaSchemas(), api.cgaCompanies()]);
      setAnalyses(a); setSchemas(s); setCompanies(c);
    } catch (e) { setErr(api.apiError(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function createAnalysis(form) {
    try {
      await api.cgaAnalysisCreate(form);
      toast.success('Analisi creata'); setDlg(false); load();
    } catch (e) { toast.error(api.apiError(e)); }
  }

  if (loading) return <div><PageHeader title="Analisi" /><LoadingTable /></div>;
  if (err) return <div><PageHeader title="Analisi" /><ErrorState message={err} onRetry={load} /></div>;

  const canCreate = schemas.length > 0 && companies.length > 0;

  return (
    <div data-testid="cga-analisi-page">
      <PageHeader title="Analisi di gruppo" description="Consolidato multi-società con versioning e confronto progressivo" />


      {analyses.length === 0 ? (
        <EmptyState title="Nessuna analisi" description="Le analisi (Base 2025) vengono inizializzate automaticamente dal sistema all'avvio." />
      ) : (
        <DataTable
          rows={analyses}
          getRowId={(r) => r.id}
          columns={[
            { key: 'name', header: 'Nome', render: (r) => <button className="font-medium hover:underline" onClick={() => nav(`/analisi-cg/analisi/${r.id}`)} data-testid="analysis-row-link">{r.name}</button> },
            { key: 'year', header: 'Anno', align: 'right' },
            { key: 'period', header: 'Periodo', sortable: false, render: (r) => `${MONTHS[(r.from_month || 1) - 1]}–${MONTHS[(r.to_month || 12) - 1]}` },
            { key: 'companies', header: 'Società', sortable: false, render: (r) => (r.company_ids || []).length },
            { key: 'baseline', header: 'Tipo', sortable: false, render: (r) => r.is_baseline ? <Badge variant="secondary"><Lock className="h-3 w-3 mr-1" />Base</Badge> : <Badge variant="outline">Periodo</Badge> },
            { key: 'versions_count', header: 'Versioni', align: 'right' },
            { key: 'status', header: 'Stato', render: (r) => <Badge variant={r.status === 'computed' ? 'secondary' : 'outline'}>{r.status === 'computed' ? 'Calcolata' : 'Bozza'}</Badge> },
          ]}
          actions={{ canWrite: true, onEdit: (r) => nav(`/analisi-cg/analisi/${r.id}`) }}
          emptyText="Nessuna analisi."
        />
      )}

      {dlg && <AnalysisDialog schemas={schemas} companies={companies} analyses={analyses} isAdmin={isAdmin} onClose={() => setDlg(false)} onSave={createAnalysis} />}
    </div>
  );
}

function AnalysisDialog({ schemas, companies, analyses, isAdmin, onClose, onSave }) {
  const [f, setF] = useState({
    name: '', year: new Date().getFullYear(), from_month: 1, to_month: 12,
    company_ids: [], schema_id: schemas[0]?.id || '', prev_analysis_id: 'none', eliminate: true, is_baseline: false,
  });
  const toggleCompany = (id) => setF((s) => ({ ...s, company_ids: s.company_ids.includes(id) ? s.company_ids.filter((x) => x !== id) : [...s.company_ids, id] }));
  const submit = () => {
    const payload = { ...f, year: Number(f.year), from_month: Number(f.from_month), to_month: Number(f.to_month),
      prev_analysis_id: f.prev_analysis_id === 'none' ? null : f.prev_analysis_id };
    onSave(payload);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="form-modal" className="max-w-lg">
        <DialogHeader><DialogTitle>Nuova analisi</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          <div><Label>Nome</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="es. Base 2025 / H1 2026" data-testid="analysis-name-input" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Anno</Label><Input type="number" value={f.year} onChange={(e) => setF({ ...f, year: e.target.value })} data-testid="analysis-year-input" /></div>
            <div><Label>Dal mese</Label>
              <Select value={String(f.from_month)} onValueChange={(v) => setF({ ...f, from_month: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Al mese</Label>
              <Select value={String(f.to_month)} onValueChange={(v) => setF({ ...f, to_month: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Schema di riclassifica</Label>
            <Select value={f.schema_id} onValueChange={(v) => setF({ ...f, schema_id: v })}>
              <SelectTrigger data-testid="analysis-schema-select"><SelectValue /></SelectTrigger>
              <SelectContent>{schemas.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Società da consolidare</Label>
            <div className="mt-1 space-y-1.5 rounded-lg border p-3 max-h-40 overflow-y-auto">
              {companies.map((c) => (
                <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={f.company_ids.includes(c.id)} onCheckedChange={() => toggleCompany(c.id)} data-testid={`analysis-company-${c.code}`} />
                  <span>{c.code} — {c.name}</span>
                </label>
              ))}
            </div>
          </div>
          <div><Label>Periodo precedente (confronto)</Label>
            <Select value={f.prev_analysis_id} onValueChange={(v) => setF({ ...f, prev_analysis_id: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="none">Nessuno</SelectItem>{analyses.filter((a) => !a.is_baseline).map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between"><Label>Applica elisioni intercompany</Label><Switch checked={f.eliminate} onCheckedChange={(v) => setF({ ...f, eliminate: v })} /></div>
          {isAdmin && <div className="flex items-center justify-between"><Label>Base (baseline bloccata)</Label><Switch checked={f.is_baseline} onCheckedChange={(v) => setF({ ...f, is_baseline: v })} data-testid="analysis-baseline-switch" /></div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={submit} data-testid="analysis-save-btn">Crea</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
